import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { PagingType, UTable } from '@components/common/UTable/UTable';
import { App } from 'antd';
import { useMemo, useState } from 'react';

import {
  useDeleteAnimalSpeciesTransitionMutation,
  useGetDictionariesQuery,
  useGetDictionaryQuery,
  usePersistAnimalSpeciesTransitionMutation,
  usePersistEventProtocolTypeMutation,
} from '@/api/dictionaries/api';
import {
  AnimalSpeciesTransition,
  EventProtocolTypeDictionaryItem,
} from '@/api/dictionaries/types';
import {
  protocolTypeColumns,
  transitionColumns,
} from '@/pages/Administration/components/animalGroups/columns.config';
import {
  ANY_SEX,
  protocolTypeSchema,
  transitionSchema,
} from '@/pages/Administration/components/animalGroups/modal.config';
import { PageableResponse } from '@/shared/api/types';
import { administration } from '@/shared/security/permissions/administration';
import { IndividualSex } from '@/shared/vaccination/enums';
import { mapTableIndex, mapTableIndexWithPage } from '@/utils/utils';

type TransitionFormValues = Omit<AnimalSpeciesTransition, 'id' | 'sex'> & {
  sex: IndividualSex | typeof ANY_SEX;
};

export const AnimalGroupsPage = () => {
  const { message } = App.useApp();
  const [paging, setPaging] = useState<PagingType>({ page: 0, size: 10 });

  const [isTransitionModalOpen, setIsTransitionModalOpen] = useState<boolean>(false);
  const [currentTransition, setCurrentTransition] =
    useState<AnimalSpeciesTransition | null>(null);
  const [transitionToDelete, setTransitionToDelete] =
    useState<AnimalSpeciesTransition | null>(null);
  const [currentProtocolType, setCurrentProtocolType] =
    useState<EventProtocolTypeDictionaryItem | null>(null);

  const { data: dictionaries, isFetching: isDictionariesFetching } =
    useGetDictionariesQuery();
  const { data: transitions, isFetching: isTransitionsFetching } = useGetDictionaryQuery({
    dictionaryName: 'animalSpeciesTransition',
    pageable: paging,
  });

  const [persistTransition] = usePersistAnimalSpeciesTransitionMutation();
  const [deleteTransition] = useDeleteAnimalSpeciesTransitionMutation();
  const [persistProtocolType] = usePersistEventProtocolTypeMutation();

  const transitionModalData = useMemo(
    () =>
      currentTransition
        ? { ...currentTransition, sex: currentTransition.sex ?? ANY_SEX }
        : { sex: ANY_SEX },
    [currentTransition],
  );

  const protocolTypes = useMemo(
    () =>
      mapTableIndex(
        [
          ...((dictionaries?.EVENT_PROTOCOL_TYPE ||
            []) as EventProtocolTypeDictionaryItem[]),
        ].sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0)),
      ),
    [dictionaries],
  );

  const closeTransitionModal = () => {
    setIsTransitionModalOpen(false);
    setCurrentTransition(null);
  };

  const submitTransition = async (values: TransitionFormValues) => {
    await persistTransition({
      id: currentTransition?.id,
      fromSpeciesId: values.fromSpeciesId,
      sex: values.sex === ANY_SEX ? null : values.sex,
      toSpeciesId: values.toSpeciesId,
      maxAgeDays: values.maxAgeDays,
    }).unwrap();
    message.success('Данные успешно сохранены');
    closeTransitionModal();
  };

  const submitProtocolType = async (values: { animalSpeciesId?: string | null }) => {
    if (!currentProtocolType) return;

    await persistProtocolType({
      id: currentProtocolType.id,
      name: currentProtocolType.name,
      orderNumber: currentProtocolType.orderNumber,
      animalSpeciesId: values.animalSpeciesId ?? null,
    }).unwrap();
    message.success('Данные успешно сохранены');
    setCurrentProtocolType(null);
  };

  return (
    <>
      <PageTitle title={'Переходы между группами'} />
      <UTable
        size={'small'}
        data={mapTableIndexWithPage(
          (transitions as PageableResponse<AnimalSpeciesTransition>)?.content,
          paging,
        )}
        columns={transitionColumns(dictionaries?.dictMap)}
        loading={isTransitionsFetching || isDictionariesFetching}
        paging={paging}
        onPagingChange={setPaging}
        total={(transitions as PageableResponse<AnimalSpeciesTransition>)?.totalElements}
        buttons={[
          {
            addBtn: true,
            action: () => {
              setCurrentTransition(null);
              setIsTransitionModalOpen(true);
            },
            security: {
              permissions: administration.animalGroups.edit,
            },
          },
        ]}
        actions={() => [
          {
            icon: <EditOutlined />,
            tooltip: 'Редактировать',
            action: (record: AnimalSpeciesTransition) => {
              setCurrentTransition(record);
              setIsTransitionModalOpen(true);
            },
            security: {
              permissions: administration.animalGroups.edit,
            },
          },
          {
            icon: <DeleteOutlined />,
            tooltip: 'Удалить',
            action: (record: AnimalSpeciesTransition) => setTransitionToDelete(record),
            security: {
              permissions: administration.animalGroups.edit,
            },
          },
        ]}
      />

      <PageTitle title={'Схемы мероприятий по группам'} subTitle={true} />
      <UTable
        size={'small'}
        data={protocolTypes}
        columns={protocolTypeColumns(dictionaries?.dictMap)}
        loading={isDictionariesFetching}
        actions={() => [
          {
            icon: <EditOutlined />,
            tooltip: 'Редактировать',
            action: (record: EventProtocolTypeDictionaryItem) =>
              setCurrentProtocolType(record),
            security: {
              permissions: administration.animalGroups.edit,
            },
          },
        ]}
      />

      <Modal
        open={isTransitionModalOpen}
        mode={'form'}
        title={currentTransition ? 'Редактировать переход' : 'Добавить переход'}
        data={transitionModalData}
        schema={transitionSchema(dictionaries)}
        onSubmit={submitTransition}
        onCancel={closeTransitionModal}
        width={'375px'}
        layout={{
          formLayout: 'vertical',
          itemLayout: 'vertical',
        }}
        destroyOnHidden={true}
      />

      <Modal
        open={Boolean(currentProtocolType)}
        mode={'form'}
        title={currentProtocolType?.name}
        data={{ animalSpeciesId: currentProtocolType?.animalSpeciesId ?? undefined }}
        schema={protocolTypeSchema(dictionaries)}
        onSubmit={submitProtocolType}
        onCancel={() => setCurrentProtocolType(null)}
        width={'375px'}
        layout={{
          formLayout: 'vertical',
          itemLayout: 'vertical',
        }}
        destroyOnHidden={true}
      />

      <Modal
        open={Boolean(transitionToDelete)}
        mode={'confirm'}
        text={
          'Вы уверены, что хотите удалить выбранные данные? При выборе варианта «Да» данные восстановлению не подлежат'
        }
        onSubmit={async () => {
          if (transitionToDelete) {
            await deleteTransition({ id: transitionToDelete.id }).unwrap();
          }
          setTransitionToDelete(null);
        }}
        onCancel={() => setTransitionToDelete(null)}
      />
    </>
  );
};
